import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { VanityAPI } from '../services/api';
import TestImagePage from './TestImagePage';
import AdminHighlights from './AdminHighlights';
import StandardImageUploader from '../components/StandardImageUploader';
import RichTextEditor from '../components/RichTextEditor';
import Cropper from 'react-easy-crop';
import SEOPanel from '../components/SEOPanel';
import { unescapeHtml } from '../utils/unescapeHtml';
import { MediaAsset, Issue, HighlightArticle, VanityPage, ArticleCategory, FooterConfig, HomepageConfig, AdminUser } from '../types';

interface AdminAtelierProps {
   onLogout: () => void;
}


type Tab = 'dashboard' | 'homepage' | 'current_issue' | 'all_magazines' | 'pages' | 'categories' | 'users' | 'footer' | 'highlights';

const VALID_TABS: Tab[] = ['dashboard', 'homepage', 'current_issue', 'all_magazines', 'highlights', 'pages', 'categories', 'users', 'footer'];

const AdminAtelier: React.FC<AdminAtelierProps> = ({ onLogout }) => {
   const navigate = useNavigate();
   const location = useLocation();

   const tabFromUrl = (): Tab => {
      const seg = location.pathname.replace('/vanityadmin', '').replace(/^\//, '') as Tab;
      return VALID_TABS.includes(seg) ? seg : 'dashboard';
   };

   const activeTab = tabFromUrl();
   const setActiveTab = (tab: Tab) => navigate(`/vanityadmin/${tab}`, { replace: false });
   const [logs, setLogs] = useState<string[]>(['Atelier Registry v1.5 Online.']);

   // Data State
   const [allMagazines, setAllMagazines] = useState<Issue[]>([]);
   const [allPages, setAllPages] = useState<VanityPage[]>([]);
   const [allCategories, setAllCategories] = useState<ArticleCategory[]>([]);
   const [allUsers, setAllUsers] = useState<AdminUser[]>([]);
   const [mesh, setMesh] = useState<MediaAsset[]>([]);
   const [footerConfig, setFooterConfig] = useState<FooterConfig | null>(null);
   const [homepageConfig, setHomepageConfig] = useState<HomepageConfig | null>(null);
   const [issueHighlights, setIssueHighlights] = useState<HighlightArticle[]>([]);
   const [allHighlights, setAllHighlights] = useState<HighlightArticle[]>([]);

   // UI State
   const [editingMag, setEditingMag] = useState<Issue | null>(null);
   const [editingPage, setEditingPage] = useState<VanityPage | null>(null);
   const [magSubTab, setMagSubTab] = useState<'general' | 'gallery' | 'highlights' | 'seo'>('general');
   const [pageSubTab, setPageSubTab] = useState<'content' | 'navigation' | 'seo'>('content');

   const [editingCategory, setEditingCategory] = useState<string | null>(null);
   const [isCreatingCategory, setIsCreatingCategory] = useState(false);
   const [catTempName, setCatTempName] = useState('');
   const [catTempSlug, setCatTempSlug] = useState('');
   const [isSaving, setIsSaving] = useState(false);
   const [uploadTarget, setUploadTarget] = useState<string | null>(null);
   const [moduleTargetId, setModuleTargetId] = useState<string | null>(null);
   const [confirmModal, setConfirmModal] = useState<{ title: string, msg: string, onConfirm: () => void } | null>(null);
   const [toast, setToast] = useState<string | null>(null);
   const [lightbox, setLightbox] = useState<string | null>(null);

   // Highlights Modal
   const [editingHighlightId, setEditingHighlightId] = useState<string | null>(null);
   const [highlightTab, setHighlightTab] = useState<'content' | 'seo'>('content');

   // User Management State
   const [passwordModal, setPasswordModal] = useState<{ user: AdminUser } | null>(null);
   const [newPassword, setNewPassword] = useState('');
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

   // GOLDEN STANDARD CROP STATE
   const [cropFile, setCropFile] = useState<string | null>(null);
   const [cropRawFile, setCropRawFile] = useState<File | null>(null);
   const [crop, setCrop] = useState({ x: 0, y: 0 });
   const [zoom, setZoom] = useState(1);
   const [aspect, setAspect] = useState(3 / 4); // Default to Cover Portrait
   const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

   const [pdfUploadProgress, setPdfUploadProgress] = useState<number | null>(null);

   // Standard Uploaders
   const [showStandardUploader, setShowStandardUploader] = useState(false);
   const [stashedFile, setStashedFile] = useState<File | null>(null);
   const sharedInputRef = useRef<HTMLInputElement>(null);
   const legacyFileInputRef = useRef<HTMLInputElement>(null);

   useEffect(() => {
      const init = async () => {
         await VanityAPI.bootstrap();
         loadRegistry();
      };
      init();
   }, []);

   useEffect(() => {
      if (editingMag) {
         VanityAPI.getHighlights(editingMag.id).then(setIssueHighlights);
      }
   }, [editingMag]);

   // Alt Text Auto-Save (Homepage)
   useEffect(() => {
      const handler = setTimeout(() => {
         if (homepageConfig?.heroImageId && homepageConfig?.heroAltText !== undefined) {
            // Only save if it's different/dirty? 
            // Actually verifying if changed from DB is hard without extra state.
            // We'll trust the debounce and just save (low cost).
            VanityAPI.updateImageAltText(homepageConfig.heroImageId, homepageConfig.heroAltText)
               .then(() => console.log("Alt Text Autosaved"))
               .catch(err => console.error("Alt Text Save Failed", err));
         }
      }, 1500);
      return () => clearTimeout(handler);
   }, [homepageConfig?.heroAltText, homepageConfig?.heroImageId]);

   const handleSaveInlineHighlight = async (highlight: HighlightArticle) => {
      setIsSaving(true);
      try {
         // Rule 61: SEO Sync Verification
         const payload = {
            ...highlight,
            seoTitle: highlight.seoTitle || highlight.title || '',
            seoDescription: highlight.seoDescription || highlight.text || '',
            seoKeywords: highlight.seoKeywords || '',
            ogTitle: highlight.ogTitle || highlight.seoTitle || highlight.title || '',
            ogDescription: highlight.ogDescription || highlight.seoDescription || highlight.text || '',
            canonicalUrl: highlight.canonicalUrl || ''
         };
         await VanityAPI.saveHighlight(payload);
         showToast('Story Saved Successfully');
      } catch (err: any) {
         showToast(`Error: ${err.message}`);
      } finally {
         setIsSaving(false);
      }
   };

   const loadRegistry = async () => {
      try {
         const [mags, pages, cats, ftr, hme, usrs, hilites] = await Promise.all([
            VanityAPI.getAdminMagazines(), // Use Admin Endpoint to see drafts
            VanityAPI.getPages(),
            VanityAPI.getCategories(),
            VanityAPI.getFooterConfig(),
            VanityAPI.getHomepageConfig(),
            VanityAPI.getAdminUsers(),
            VanityAPI.getAdminHighlights()
         ]);
         setAllMagazines(mags.sort((a, b) => (a.isCurrent ? -1 : 1)));
         setAllPages(pages);
         setAllCategories(cats);
         setAllUsers(usrs);
         setAllHighlights(hilites);
         setMesh([]); // Legacy removed
         setFooterConfig(ftr);
         setHomepageConfig(hme);
      } catch (err) {
         console.error("Load Registry Error:", err);
         addLog("Registry sync failure.");
      }
   };

   const showToast = (msg: string) => {
      setToast(msg);
      setTimeout(() => setToast(null), 3000);
   };

   const addLog = (msg: string) => setLogs(p => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...p.slice(0, 10)]);

   const handleSaveMag = async () => {
      if (!editingMag) return;
      setIsSaving(true);
      try {
         // Rule 33 & 61: Explicit Payload Mapping & SEO Sync Verification
         // SURGICAL REMOVAL: Destructure to ensure seoData is never passed
         const { seoData, ...cleanMag } = editingMag as any;

         const payload = {
            ...cleanMag,
            editorialText: editingMag.editorialText,
            // SEO & Big Six Enforcement
            seoTitle: editingMag.seoTitle || editingMag.title || '',
            seoDescription: editingMag.seoDescription || editingMag.summary || '',
            seoKeywords: editingMag.seoKeywords || '',
            ogTitle: editingMag.ogTitle || editingMag.seoTitle || editingMag.title || '',
            ogDescription: editingMag.ogDescription || editingMag.seoDescription || '',
            canonicalUrl: editingMag.canonicalUrl || '' // Backend handles auto-gen if empty, but good to flush keys
         };

         await VanityAPI.saveMagazine(payload);
         addLog(`System: Magazine "${editingMag.title}" saved successfully.`);
         // showToast(`Saved Issue: ${editingMag.title}`); // Legacy Toast

         // SMALL DISCREET FEEDBACK
         const widget = document.createElement('div');
         widget.className = 'fixed bottom-12 right-12 z-[999] bg-black text-white px-8 py-4 rounded-full shadow-2xl flex items-center gap-4 animate-bounce-in';
         widget.innerHTML = `<div class="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div><div class="font-bold text-xs uppercase tracking-widest">Database Synchronized</div>`;
         document.body.appendChild(widget);
         setTimeout(() => {
            widget.classList.add('opacity-0', 'transition-opacity', 'duration-500', 'translate-y-4');
            setTimeout(() => widget.remove(), 500);
         }, 2500);

         // Background Refresh Only - Sustain Edit Mode
         loadRegistry();
         // setEditingMag(null); // DISABLED: Persist Edit Mode
      } catch (err: any) {
         // Rule 32: Full Error Logging
         showToast(`Error: ${err.message}`);
         console.error("FULL DATABASE ERROR:", err);
      } finally {
         setIsSaving(false);
      }
   };

   const addModule = (type: 'text' | 'text-image', pos: 'left' | 'right' = 'right') => {
      if (!editingPage) return;
      const modules = editingPage.contentModules || [];
      const newModule = {
         id: crypto.randomUUID(),
         type,
         title: '',
         text: '',
         imagePosition: type === 'text-image' ? pos : undefined,
         order: modules.length
      };
      setEditingPage({ ...editingPage, contentModules: [...modules, newModule] });
      showToast('Module Added');
   };

   const moveModule = (id: string, dir: 'up' | 'down') => {
      if (!editingPage || !editingPage.contentModules) return;
      const mods = [...editingPage.contentModules];
      const idx = mods.findIndex(m => m.id === id);
      if (idx === -1) return;
      if (dir === 'up' && idx > 0) {
         [mods[idx], mods[idx - 1]] = [mods[idx - 1], mods[idx]];
      } else if (dir === 'down' && idx < mods.length - 1) {
         [mods[idx], mods[idx + 1]] = [mods[idx + 1], mods[idx]];
      }
      // Re-normalize order
      const updated = mods.map((m, i) => ({ ...m, order: i }));
      setEditingPage({ ...editingPage, contentModules: updated });
   };

   const deleteModule = (id: string) => {
      if (!editingPage || !editingPage.contentModules) return;
      setEditingPage({
         ...editingPage,
         contentModules: editingPage.contentModules.filter(m => m.id !== id)
      });
      showToast('Module Removed');
   };

   const updateModule = (id: string, data: any) => {
      if (!editingPage || !editingPage.contentModules) return;
      setEditingPage({
         ...editingPage,
         contentModules: editingPage.contentModules.map(m => m.id === id ? { ...m, ...data } : m)
      });
   };

   const handleSavePage = async () => {
      if (!editingPage) return;
      setIsSaving(true);
      try {
         // Rule 33 & 61: Explicit Payload Mapping & SEO Sync Verification
         const payload = {
            ...editingPage,
            content: editingPage.content,
            // SEO & Big Six Enforcement
            seoTitle: editingPage.seoTitle || editingPage.name || '',
            seoDescription: editingPage.seoDescription || '',
            seoKeywords: editingPage.seoKeywords || '',
            ogTitle: editingPage.ogTitle || editingPage.seoTitle || editingPage.name || '',
            ogDescription: editingPage.ogDescription || editingPage.seoDescription || '',
            canonicalUrl: editingPage.canonicalUrl || ''
         };

         await VanityAPI.savePage(payload);
         addLog(`Registry Update: Page "${editingPage.title}" committed.`);
         showToast(`Saved Page: ${editingPage.title}`);
         await loadRegistry();
         setEditingPage(null);
      } catch (err: any) {
         // Rule 32: Full Error Logging
         showToast(`Error: ${err.message}`);
         console.error("FULL DATABASE ERROR:", err);
      } finally {
         setIsSaving(false);
      }
   };

   const handleSaveHomepage = async () => {
      if (!homepageConfig) return;
      setIsSaving(true);
      try {
         await VanityAPI.saveHomepageConfig(homepageConfig);
         addLog(`Registry Update: Homepage Identity refreshed.`);
         showToast('Homepage Identity Updated');
         await loadRegistry();
      } catch (err: any) {
         showToast(`Error: ${err.message}`);
      } finally {
         setIsSaving(false);
      }
   };

   const handleLegacyFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !uploadTarget) return;

      // INTERCEPT COVER FOR GOLDEN STANDARD CROP
      if (uploadTarget === 'cover') {
         setCropRawFile(file);
         setCropFile(URL.createObjectURL(file));
         // Reset file input value to allow re-selecting same file
         e.target.value = '';
         return;
      }

      // Legacy flow for others
      addLog(`Ingesting legacy asset: ${file.name}`);
      try {
         const isPdf = uploadTarget === 'pdf';
         if (isPdf) setPdfUploadProgress(0);
         const asset = isPdf
            ? await VanityAPI.uploadWithProgress(file, setPdfUploadProgress)
            : await VanityAPI.processAndUpload(file);
         if (uploadTarget === 'cover' && editingMag) setEditingMag({ ...editingMag, coverUrl: asset.url });
         else if (uploadTarget === 'gallery' && editingMag) setEditingMag({ ...editingMag, spreads: [...(editingMag.spreads || []), asset.url] });
         else if (isPdf && editingMag) setEditingMag({ ...editingMag, pdfUrl: asset.url });

         // Highlight Image Logic
         if (uploadTarget.startsWith('highlight:') && editingMag) {
            const hId = uploadTarget.split(':')[1];
            const updatedHighlights = (editingMag.highlights || []).map(h => h.id === hId ? { ...h, imageUrl: asset.url } : h);
            // Also update local state
            setIssueHighlights(prev => prev.map(h => h.id === hId ? { ...h, imageUrl: asset.url } : h));
            setEditingMag({ ...editingMag, highlights: updatedHighlights });
         }
         // Highlight Gallery Logic
         else if (uploadTarget.startsWith('highlight_gallery:') && editingMag) {
            const hId = uploadTarget.split(':')[1];
            setIssueHighlights(prev => prev.map(h => {
               if (h.id === hId) {
                  return { ...h, galleryImages: [...(h.galleryImages || []), asset.url] };
               }
               return h;
            }));
         }

         showToast("Asset linked.");
         loadRegistry();
      } catch (err: any) {
         showToast(`Upload Error: ${err.message}`);
      } finally {
         setUploadTarget(null);
         setPdfUploadProgress(null);
      }
   };

   // GOLDEN STANDARD HANDLERS
   const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
   };

   const handleStandardCoverUpload = async () => {
      if (!cropRawFile || !editingMag) return;
      setUploadTarget('processing_standard'); // Lock UI

      try {
         const cropJson = {
            unit: 'px',
            x: croppedAreaPixels.x,
            y: croppedAreaPixels.y,
            width: croppedAreaPixels.width,
            height: croppedAreaPixels.height
         };

         const res = await VanityAPI.uploadStandardImage(
            cropRawFile,
            'magazine_cover',
            editingMag.id || 'temp_mag_id',
            'cover',
            cropJson
         );

         // Use high-res version for cover (middle is usually 800px width, good for web)
         // Or original if not available.
         const finalUrl = res.url_middle || res.url_original;
         setEditingMag({ ...editingMag, coverUrl: finalUrl });

         addLog("Cover updated via Golden Standard Pipeline.");
         showToast("Cover Updated Standardized.");

         // Cleanup
         setCropFile(null);
         setCropRawFile(null);
      } catch (err: any) {
         showToast(`Standard Upload Failed: ${err.message}`);
      } finally {
         setUploadTarget(null);
      }
   };

   const generateSEO = () => {
      if (!editingPage) return;
      const stripped = editingPage.content.replace(/<[^>]*>/g, '').substring(0, 160);
      setEditingPage({
         ...editingPage,
         seoTitle: editingPage.title + ' | VANITY',
         seoDescription: stripped
      });
      showToast('SEO Suggestions Generated');
   };

   const generateSEOMag = () => {
      if (!editingMag) return;
      const stripped = (editingMag.summary || editingMag.editorialText || '').replace(/<[^>]*>/g, '').substring(0, 160);
      setEditingMag({
         ...editingMag,
         seoTitle: editingMag.title + ' | VANITY',
         seoDescription: stripped
      });
      showToast('SEO Suggestions Generated');
   };

   const startEditCategory = (cat: ArticleCategory) => {
      setEditingCategory(cat.id);
      setCatTempName(cat.name);
      setCatTempSlug(cat.slug);
   };

   const commitCategoryEdit = async (id: string) => {
      const original = allCategories.find(c => c.id === id);
      if (!original) return;
      try {
         const finalSlug = catTempSlug || catTempName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
         const updated = { ...original, name: catTempName, slug: finalSlug };
         await VanityAPI.saveCategory(updated);
         addLog(`Registry Update: Classification "${catTempName}" modified.`);
         await loadRegistry();
         setEditingCategory(null);
         showToast("Category Updated.");
      } catch (err: any) {
         showToast(err.message);
      }
   };

   const handleAddCategory = async () => {
      if (!catTempName.trim()) return;
      try {
         const finalSlug = catTempSlug || catTempName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
         const nc: any = {
            name: catTempName,
            slug: finalSlug,
            active: true
         };
         await VanityAPI.saveCategory(nc);
         addLog(`Registry Update: New Classification "${catTempName}" created.`);
         await loadRegistry();
         setCatTempName('');
         setCatTempSlug('');
         setIsCreatingCategory(false);
         showToast('Category Created');
      } catch (err: any) {
         showToast(err.message);
      }
   };

   console.log("DB DATA:", allMagazines); // RAW DATA CHECK
   return (
      <div className="admin-typography flex h-[100dvh] bg-[#F0F0F0] font-sans text-[#222] overflow-hidden relative">
         {/* MOBILE OVERLAY */}
         {mobileMenuOpen && (
            <div className="absolute inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm animate-fadeIn" onClick={() => setMobileMenuOpen(false)} />
         )}

         {/* SIDEBAR */}
         <aside className={`fixed inset-y-0 left-0 w-56 bg-black text-white flex flex-col z-50 shadow-2xl transition-transform duration-300 md:relative transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
            <div className="p-6 border-b border-white/5 text-center">
               <div className="font-serif text-3xl tracking-[0.1em] text-vanity-pink font-bold">VANITY</div>
               <div className="text-[10px] text-gray-500 uppercase mt-2 tracking-[0.3em] font-black opacity-60">Admin Atelier</div>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
               {(['dashboard', 'homepage', 'current_issue', 'all_magazines', 'highlights', 'pages', 'categories', 'users', 'footer'] as Tab[]).map((tab) => (
                  <button
                     key={tab}
                     onClick={async () => {
                        if (tab === 'current_issue') {
                           const current = await VanityAPI.getCurrentMagazine();
                           setEditingMag(current);
                           setEditingPage(null);
                           setActiveTab('all_magazines');
                        } else {
                           setActiveTab(tab);
                           setEditingMag(null);
                           setEditingPage(null);
                        }
                        setMobileMenuOpen(false);
                     }}
                     className={`w-full text-left px-4 py-2.5 rounded text-[11px] font-black uppercase tracking-widest transition-all active:scale-95 ${(activeTab === tab && !editingMag && !editingPage) ||
                        (tab === 'current_issue' && editingMag?.isCurrent) ||
                        (tab === 'all_magazines' && editingMag && !editingMag.isCurrent) ||
                        (tab === 'pages' && editingPage)
                        ? 'bg-vanity-pink text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/10'
                        }`}
                  >
                     {tab.toUpperCase().replace('_', ' ')}
                  </button>
               ))}
            </nav>
            <div className="p-6 border-t border-white/5">
               <button onClick={onLogout} className="w-full text-gray-500 hover:text-white text-[10px] font-black uppercase tracking-widest">Sign Out</button>
            </div>
            <div className="p-4 text-center text-[9px] text-gray-600 font-mono opacity-50">v2.2.0</div>
         </aside>

         {/* WORKSPACE */}
         <main className="flex-1 overflow-y-auto flex flex-col relative w-full bg-white">
            <header className="bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center sticky top-0 z-20 shadow-sm">
               <div className="flex items-center gap-5 text-[14px] font-black uppercase tracking-widest text-gray-400">
                  <button className="md:hidden text-black mr-2" onClick={() => setMobileMenuOpen(true)}>
                     <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                     </svg>
                  </button>
                  <button onClick={() => { setEditingMag(null); setEditingPage(null); setActiveTab('dashboard'); }} className="hover:text-vanity-pink hidden md:block">ATELIER</button>
                  <span>/</span>
                  <button onClick={() => { setEditingMag(null); setEditingPage(null); }} className="hover:text-vanity-pink">{activeTab.toUpperCase()}</button>
                  {(editingMag || editingPage) && (
                     <>
                        <span>/</span>
                        <span className="text-vanity-pink font-bold">{(editingMag?.title || editingPage?.name)?.toUpperCase()}</span>
                     </>
                  )}
               </div>
               <div className="flex gap-5">
                  {(editingMag || editingPage || activeTab === 'homepage' || activeTab === 'footer') && (
                     <button
                        onClick={() => {
                           if (editingMag) handleSaveMag();
                           else if (editingPage) handleSavePage();
                           else if (activeTab === 'homepage') handleSaveHomepage();
                           else if (activeTab === 'footer') VanityAPI.saveFooterConfig(footerConfig!).then(() => showToast('Footer Saved'));
                        }}
                        disabled={isSaving}
                        className="bg-black text-white px-8 py-2.5 text-[11px] font-black uppercase tracking-widest rounded hover:bg-vanity-pink transition-all shadow-md active:scale-95"
                     >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                     </button>
                  )}
               </div>
            </header>

            <div className="p-6 w-full max-w-[1600px] mx-auto">
               {/* Dashboard Module */}
               {!editingMag && !editingPage && activeTab === 'dashboard' && (
                  <div className="space-y-12 animate-fadeIn">
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
                        {/* Interactive Stat Boxes */}
                        {[
                           { l: 'Magazines', v: allMagazines.length, t: 'all_magazines', icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18c-2.305 0-4.408.867-6 2.292m0-14.25v14.25' },
                           { l: 'Highlights', v: allHighlights.length, t: 'highlights', icon: 'M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z' },
                           { l: 'Pages', v: allPages.length, t: 'pages', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z' }
                        ].map((s, i) => (
                           <div
                              key={i}
                              onClick={() => setActiveTab(s.t as Tab)}
                              className="bg-white p-6 border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:scale-[1.01] transition-all cursor-pointer group relative overflow-hidden"
                           >
                              <div className="absolute top-0 right-0 p-8 text-gray-100 group-hover:text-vanity-pink/10 transition-colors">
                                 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-32 h-32">
                                    <path strokeLinecap="round" strokeLinejoin="round" d={s.icon} />
                                 </svg>
                              </div>
                              <div className="relative z-10">
                                 <div className="text-[14px] font-black text-gray-400 uppercase mb-6 tracking-[0.3em] group-hover:text-vanity-pink transition-colors">{s.l}</div>
                                 <div className="text-7xl font-serif text-black font-bold">{s.v}</div>
                                 <div className="mt-8 flex items-center gap-2 text-[10px] font-black text-gray-300 uppercase tracking-widest group-hover:text-black transition-colors">
                                    Manage Section
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3 h-3">
                                       <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                    </svg>
                                 </div>
                              </div>
                           </div>
                        ))}
                     </div>

                     <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                        {/* Small Stats Grid */}
                        <div className="grid grid-cols-2 gap-6">
                           {[
                              { l: 'Admins', v: allUsers.length, t: 'users' },
                              { l: 'Tags', v: '24', t: 'highlights' }
                           ].map((s, i) => (
                              <div key={i} onClick={() => setActiveTab(s.t as Tab)} className="bg-white p-4 rounded-xl border border-gray-200 hover:border-black transition-all cursor-pointer">
                                 <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">{s.l}</div>
                                 <div className="text-2xl font-bold">{s.v}</div>
                              </div>
                           ))}
                        </div>

                        {/* Logs Display */}
                        <div className="bg-black text-gray-500 p-6 rounded-xl shadow-lg font-mono text-[11px] space-y-2 border border-white/5 max-h-[300px] overflow-y-auto">
                           <div className="text-vanity-pink font-bold mb-6 uppercase tracking-[0.4em] flex items-center gap-4">
                              <span className="w-2 h-2 bg-vanity-pink rounded-full animate-pulse"></span>
                              Master Integrity Stream
                           </div>
                           {logs.map((l, i) => <div key={i} className="opacity-80 flex gap-4">
                              <span className="text-gray-700">0{logs.length - i}</span>
                              <span className="text-gray-300">{'>>'}</span>
                              <span>{l}</span>
                           </div>)}
                        </div>
                     </div>
                  </div>
               )}

               {/* Homepage Module */}
               {!editingMag && !editingPage && activeTab === 'homepage' && homepageConfig && (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 space-y-8 animate-fadeIn">
                     <div className="border-b pb-10">
                        <h2 className="font-serif text-5xl font-bold">Homepage Identity</h2>
                     </div>
                     <div className="space-y-12">
                        <div className="space-y-4">
                           <label className="text-[14px] font-black text-gray-400 uppercase tracking-widest">Hero Overline (Top Text)</label>
                           <input type="text" value={homepageConfig.heroOverline || ''} onChange={e => setHomepageConfig({ ...homepageConfig, heroOverline: e.target.value })} className="w-full bg-gray-50 border-0 rounded-lg p-6 font-sans text-xs font-bold uppercase tracking-[0.2em] focus:ring-2 focus:ring-vanity-pink" placeholder="THE PREMIER AUSTRIAN JOURNAL" />
                        </div>
                        <div className="space-y-4">
                           <label className="text-[14px] font-black text-gray-400 uppercase tracking-widest">Hero Title (HTML)</label>
                           <textarea value={homepageConfig.heroTitle || ''} onChange={e => setHomepageConfig({ ...homepageConfig, heroTitle: e.target.value })} className="w-full border border-gray-200 p-8 rounded-2xl text-5xl font-serif focus:border-vanity-pink outline-none leading-[1.1]" rows={2} />
                        </div>
                        <div className="space-y-4">
                           <label className="text-[14px] font-black text-gray-400 uppercase tracking-widest">Hero Image Asset</label>
                           <div onClick={() => { setUploadTarget('home_hero'); sharedInputRef.current?.click(); }} className="aspect-video bg-gray-50 border-2 border-dashed border-gray-200 rounded-3xl cursor-pointer overflow-hidden flex items-center justify-center hover:border-vanity-pink transition-all group relative max-w-md shadow-lg">
                              {homepageConfig.heroImageUrl ? <img src={homepageConfig.heroImageUrl} className="w-full h-full object-cover" /> : <span className="font-bold text-gray-300">Link Asset</span>}
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                 <span className="text-white font-black uppercase text-[10px] tracking-widest">Replace Hero</span>
                              </div>
                           </div>
                           <div className="mt-4">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Visual Description (Alt Text)</label>
                              <input
                                 type="text"
                                 value={homepageConfig.heroAltText || ''}
                                 onChange={e => setHomepageConfig({ ...homepageConfig, heroAltText: e.target.value })}
                                 className="w-full max-w-md bg-gray-50 border-0 rounded-lg p-4 font-sans text-xs font-bold focus:ring-2 focus:ring-vanity-pink outline-none"
                                 placeholder="Describe image for SEO & Accessibility..."
                              />
                           </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                           <div className="space-y-4 p-8 bg-gray-50 rounded-2xl">
                              <label className="text-[11px] font-black text-vanity-pink uppercase tracking-widest">Primary Button</label>
                              <input type="text" value={homepageConfig.cta1Text || ''} onChange={e => setHomepageConfig({ ...homepageConfig, cta1Text: e.target.value })} className="w-full border p-4 rounded-xl font-bold" />
                              <input type="text" value={homepageConfig.cta1Url || ''} onChange={e => setHomepageConfig({ ...homepageConfig, cta1Url: e.target.value })} className="w-full border p-4 rounded-xl font-mono text-sm" />
                           </div>
                           <div className="space-y-4 p-8 bg-gray-50 rounded-2xl">
                              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Secondary Button</label>
                              <input type="text" value={homepageConfig.cta2Text || ''} onChange={e => setHomepageConfig({ ...homepageConfig, cta2Text: e.target.value })} className="w-full border p-4 rounded-xl font-bold" />
                              <input type="text" value={homepageConfig.cta2Url || ''} onChange={e => setHomepageConfig({ ...homepageConfig, cta2Url: e.target.value })} className="w-full border p-4 rounded-xl font-mono text-sm" />
                           </div>
                        </div>

                        {/* HOMEPAGE SEO */}
                        <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                           <SEOPanel
                              seoTitle={homepageConfig.seoTitle || ''}
                              seoDescription={homepageConfig.seoDescription || ''}
                              seoKeywords={homepageConfig.seoKeywords || ''}
                              ogTitle={homepageConfig.ogTitle || ''}
                              ogDescription={homepageConfig.ogDescription || ''}
                              canonicalUrl={homepageConfig.canonicalUrl || ''}
                              // seoData removed
                              slug="" // Homepage is root
                              contentForAI={`${homepageConfig.heroTitle} ${homepageConfig.heroOverline}`}
                              baseUrl="https://vanity.co.at"
                              onChange={(fields) => setHomepageConfig(prev => {
                                 if (!prev) return null;
                                 return {
                                    ...prev,
                                    ...fields
                                 };
                              })}
                           />
                        </div>
                     </div>
                  </div>
               )}

               {/* Magazine Editor Module */}
               {editingMag && (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden animate-fadeIn">
                     <div className="bg-gray-50 border-b p-4 flex gap-4">
                        {(['general', 'gallery', 'highlights', 'seo'] as const).map(t => (
                           <button key={t} onClick={() => setMagSubTab(t)} className={`px-6 py-2 rounded text-[11px] font-black uppercase tracking-widest transition-all ${magSubTab === t ? 'bg-black text-white shadow-md' : 'text-gray-400 hover:text-black'}`}>
                              {t}
                           </button>
                        ))}
                     </div>
                     <div className="p-8">
                        {magSubTab === 'general' && (
                           <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                              <div className="lg:col-span-8 space-y-8">
                                 <div className="grid grid-cols-2 gap-8">
                                    <div className="space-y-2">
                                       <label className="text-[12px] font-black text-gray-400 uppercase tracking-widest">Anthology Title</label>
                                       <input type="text" value={editingMag.title || ''} onChange={e => setEditingMag({ ...editingMag, title: e.target.value })} className="w-full border border-gray-200 p-4 rounded-lg focus:border-vanity-pink outline-none font-bold text-xl shadow-inner" />
                                    </div>
                                    <div className="space-y-2">
                                       <label className="text-[12px] font-black text-gray-400 uppercase tracking-widest">Temporal Label</label>
                                       <input type="text" value={editingMag.periodText || ''} onChange={e => setEditingMag({ ...editingMag, periodText: e.target.value })} className="w-full border border-gray-200 p-4 rounded-lg focus:border-vanity-pink outline-none font-bold text-xl shadow-inner" placeholder="Winter 2025" />
                                    </div>
                                 </div>

                                 {/* NEW: Extra Data Fields (Season, Year, Range) */}
                                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-1">
                                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Season</label>
                                       <input type="text" value={editingMag.season || ''} onChange={e => setEditingMag({ ...editingMag, season: e.target.value })} className="w-full p-2.5 border rounded-lg font-bold text-sm" placeholder="Winter" />
                                    </div>
                                    <div className="space-y-1">
                                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Year</label>
                                       <input type="number" value={editingMag.year || ''} onChange={e => setEditingMag({ ...editingMag, year: parseInt(e.target.value) })} className="w-full p-2.5 border rounded-lg font-bold text-sm" placeholder="2025" />
                                    </div>
                                    <div className="space-y-1">
                                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Issue Date / Range</label>
                                       <input type="text" value={editingMag.issueDate || ''} onChange={e => setEditingMag({ ...editingMag, issueDate: e.target.value })} className="w-full p-2.5 border rounded-lg font-bold text-sm" placeholder="01/25 - 03/25" />
                                    </div>
                                 </div>

                                 <div className="space-y-2">
                                    <label className="text-[12px] font-black text-gray-400 uppercase tracking-widest">URL Slug (e.g., winter-2025)</label>
                                    <div className="flex gap-2">
                                       <input type="text" value={editingMag.slug || ''} onChange={e => setEditingMag({ ...editingMag, slug: e.target.value })} className="w-full border border-gray-200 p-3 rounded-lg focus:border-vanity-pink outline-none font-mono text-base shadow-inner" placeholder="auto-generated-if-empty" />
                                       <button onClick={() => setEditingMag({ ...editingMag, slug: editingMag.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') })} className="px-4 rounded-lg bg-gray-100 hover:bg-black hover:text-white font-black uppercase text-[9px] tracking-widest transition-all">Generate</button>
                                    </div>
                                 </div>
                                 <div className="space-y-2">
                                    <label className="text-[12px] font-black text-gray-400 uppercase tracking-widest">Editorial Summary (Hero Quote)</label>
                                    <RichTextEditor value={editingMag.summary || ''} onChange={val => setEditingMag({ ...editingMag, summary: val })} height="100px" />
                                 </div>

                                 <div className="space-y-2">
                                    <label className="text-[12px] font-black text-gray-400 uppercase tracking-widest">Main Anthology Narrative (Editorial)</label>
                                    <RichTextEditor name="editorialText" value={editingMag.editorialText || ''} onChange={val => setEditingMag({ ...editingMag, editorialText: val })} height="300px" />
                                 </div>

                                 {/* Dynamic Columns */}
                                 <div className="grid grid-cols-2 gap-6 pt-6 border-t border-gray-100">
                                    <div className="space-y-2">
                                       <input name="col1Title" type="text" value={editingMag.col1Title || ''} onChange={e => setEditingMag({ ...editingMag, col1Title: e.target.value })} placeholder="Column 1 Title" className="w-full border-b p-1.5 font-serif font-bold text-lg" />
                                       <textarea name="col1Text" rows={3} value={editingMag.col1Text || ''} onChange={e => setEditingMag({ ...editingMag, col1Text: e.target.value })} placeholder="Column 1 Text..." className="w-full bg-gray-50 p-3 rounded-lg text-sm" />
                                    </div>
                                    <div className="space-y-2">
                                       <input name="col2Title" type="text" value={editingMag.col2Title || ''} onChange={e => setEditingMag({ ...editingMag, col2Title: e.target.value })} placeholder="Column 2 Title" className="w-full border-b p-1.5 font-serif font-bold text-lg" />
                                       <textarea name="col2Text" rows={3} value={editingMag.col2Text || ''} onChange={e => setEditingMag({ ...editingMag, col2Text: e.target.value })} placeholder="Column 2 Text..." className="w-full bg-gray-50 p-3 rounded-lg text-sm" />
                                    </div>
                                 </div>
                                 <div className="h-10"></div>
                              </div>
                              <div className="lg:col-span-4 space-y-8">
                                 <div onClick={() => { setUploadTarget('cover'); sharedInputRef.current?.click(); }} className="aspect-[3/4] bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center overflow-hidden hover:border-vanity-pink transition-all relative group shadow-lg cursor-pointer">
                                    {editingMag.coverUrl ? <img src={editingMag.coverUrl} className="w-full h-full object-cover" /> : <span className="font-bold text-gray-300">Upload Cover</span>}
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                       <span className="text-white font-black uppercase text-[10px] tracking-widest">Replace Cover</span>
                                    </div>
                                 </div>

                                 {/* Cover Alt Text */}
                                 <div className="mt-2 space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block text-center">Cover Visual Description (Alt)</label>
                                    <input
                                       type="text"
                                       value={editingMag.coverAltText || ''}
                                       onChange={(e) => {
                                          setEditingMag({ ...editingMag, coverAltText: e.target.value });
                                          // Debounced Auto-Save
                                          if ((window as any).coverAltTimer) clearTimeout((window as any).coverAltTimer);
                                          (window as any).coverAltTimer = setTimeout(async () => {
                                             if (editingMag.coverImageId) {
                                                await VanityAPI.updateImageAltText(editingMag.coverImageId, e.target.value);
                                                console.log('Cover Alt Saved');
                                             }
                                          }, 1500);
                                       }}
                                       className="w-full text-xs border border-gray-200 p-2 rounded-lg focus:border-vanity-pink outline-none text-center bg-white shadow-sm"
                                       placeholder="Describe for accessibility..."
                                    />
                                 </div>

                                 {/* PDF Upload */}
                                 <div
                                    onClick={() => { if (pdfUploadProgress !== null) return; setUploadTarget('pdf'); legacyFileInputRef.current?.click(); }}
                                    className={`bg-gray-50 border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center gap-2 transition-all overflow-hidden ${pdfUploadProgress !== null ? 'border-vanity-pink cursor-wait' : 'border-gray-200 hover:border-vanity-pink cursor-pointer group'}`}
                                 >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-[10px] transition-colors ${pdfUploadProgress !== null ? 'bg-vanity-pink/10 text-vanity-pink' : 'bg-red-50 text-red-500'}`}>PDF</div>
                                    <div className="text-center w-full">
                                       {pdfUploadProgress !== null ? (
                                          <div className="w-full space-y-1.5">
                                             <div className="text-[10px] font-bold uppercase tracking-widest text-vanity-pink">
                                                Uploading... {pdfUploadProgress}%
                                             </div>
                                             <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                <div
                                                   className="bg-vanity-pink h-1.5 rounded-full transition-all duration-200"
                                                   style={{ width: `${pdfUploadProgress}%` }}
                                                />
                                             </div>
                                          </div>
                                       ) : (
                                          <>
                                             <div className="font-bold text-[10px] uppercase tracking-widest text-gray-400 group-hover:text-black transition-colors">{editingMag.pdfUrl ? 'Update PDF' : 'Upload PDF'}</div>
                                             {editingMag.pdfUrl && (
                                                <a
                                                   href={editingMag.pdfUrl}
                                                   target="_blank"
                                                   rel="noopener noreferrer"
                                                   onClick={(e) => e.stopPropagation()}
                                                   className="text-[9px] text-green-600 font-bold mt-1 hover:underline block"
                                                >
                                                   ✓ View PDF
                                                </a>
                                             )}
                                          </>
                                       )}
                                    </div>
                                 </div>

                                 <div className="bg-black p-10 rounded-3xl text-white space-y-8 shadow-xl mt-8">
                                    <label className="flex items-center gap-6 cursor-pointer">
                                       <input type="checkbox" checked={editingMag.isCurrent} onChange={e => setEditingMag({ ...editingMag, isCurrent: e.target.checked })} className="w-6 h-6 accent-vanity-pink" />
                                       <span className="font-black uppercase text-xs tracking-widest">Primary Anthology Node</span>
                                    </label>
                                    <label className="flex items-center gap-6 cursor-pointer">
                                       <input type="checkbox" checked={editingMag.status === 'published'} onChange={e => setEditingMag({ ...editingMag, status: e.target.checked ? 'published' : 'draft' })} className="w-6 h-6 accent-vanity-pink" />
                                       <span className="font-black uppercase text-xs tracking-widest">{editingMag.status === 'published' ? 'Active Public Visibility' : 'Hidden (Draft)'}</span>
                                    </label>
                                 </div>
                              </div>
                           </div>
                        )}

                        {magSubTab === 'gallery' && (
                           <div className="space-y-12">
                              <button onClick={() => { setUploadTarget('gallery'); legacyFileInputRef.current?.click(); }} className="bg-black text-white px-10 py-4 rounded-full text-[13px] font-black uppercase tracking-widest shadow-xl">+ Add Visual Spread</button>
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10">
                                 {(editingMag.spreads || []).map((s, idx) => (
                                    <div key={idx} className="aspect-[3/4] bg-gray-100 border rounded-2xl overflow-hidden relative group shadow-lg">
                                       <img src={s} className="w-full h-full object-cover" />
                                       <button onClick={() => setEditingMag({ ...editingMag, spreads: (editingMag.spreads || []).filter((_, i) => i !== idx) })} className="absolute top-4 right-4 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">✕</button>
                                    </div>
                                 ))}
                              </div>
                           </div>
                        )}
                        {magSubTab === 'highlights' && (
                           <div className="space-y-12 animate-fadeIn">
                              <div className="flex justify-between items-center border-b pb-8">
                                 <h3 className="font-serif text-3xl font-bold italic">Issue Highlight Stories</h3>
                              </div>
                              <div className="space-y-6">
                                 {issueHighlights.map((h, hIdx) => (
                                    <div key={h.id} className="bg-white border border-gray-100 p-8 rounded-3xl grid grid-cols-1 md:grid-cols-12 gap-8 shadow-sm hover:shadow-lg transition-all items-start relative group">
                                       {/* Reorder Controls */}
                                       <div className="absolute -left-12 top-8 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <button onClick={() => {
                                             const newH = [...issueHighlights];
                                             if (hIdx > 0) { [newH[hIdx], newH[hIdx - 1]] = [newH[hIdx - 1], newH[hIdx]]; setIssueHighlights(newH.map((x, i) => ({ ...x, displayOrder: i }))); }
                                          }} className="bg-gray-100 p-2 rounded-full hover:bg-black hover:text-white">▲</button>
                                          <button onClick={() => {
                                             const newH = [...issueHighlights];
                                             if (hIdx < newH.length - 1) { [newH[hIdx], newH[hIdx + 1]] = [newH[hIdx + 1], newH[hIdx]]; setIssueHighlights(newH.map((x, i) => ({ ...x, displayOrder: i }))); }
                                          }} className="bg-gray-100 p-2 rounded-full hover:bg-black hover:text-white">▼</button>
                                       </div>

                                       <div className="md:col-span-3">
                                          <div onClick={() => {
                                             setUploadTarget('highlight:' + h.id);
                                             legacyFileInputRef.current?.click();
                                          }} className="aspect-square bg-gray-50 rounded-2xl overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center border border-gray-100">
                                             {h.imageUrl ? <img src={h.imageUrl} className="w-full h-full object-cover" /> : <span className="text-[9px] font-black uppercase text-gray-300">Add Visual</span>}
                                          </div>
                                       </div>
                                       <div className="md:col-span-9 space-y-4">
                                          <div className="flex gap-4">
                                             <input type="text" value={h.title || ''} onChange={e => setIssueHighlights(issueHighlights.map(ih => ih.id === h.id ? { ...ih, title: e.target.value } : ih))} className="flex-1 bg-gray-50 border-0 p-4 rounded-xl font-serif font-bold text-xl focus:bg-white focus:ring-1 focus:ring-vanity-pink transition-all" placeholder="Story Title" />
                                             <select value={h.category || ''} onChange={e => setIssueHighlights(issueHighlights.map(ih => ih.id === h.id ? { ...ih, category: e.target.value } : ih))} className="w-40 border-0 bg-gray-50 p-4 rounded-xl font-black uppercase text-xs focus:bg-white focus:ring-1 focus:ring-vanity-pink transition-all">
                                                {allCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                             </select>
                                          </div>
                                          <textarea rows={2} value={h.text || ''} onChange={e => setIssueHighlights(issueHighlights.map(ih => ih.id === h.id ? { ...ih, text: e.target.value } : ih))} className="w-full bg-gray-50 border-0 p-4 rounded-xl text-sm text-gray-600 focus:bg-white focus:ring-1 focus:ring-vanity-pink transition-all" placeholder="Short description..." />

                                          {/* Action Bar */}
                                          <div className="flex justify-between items-center pt-2">
                                             <button onClick={() => setEditingHighlightId(h.id)} className="bg-black text-white px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-vanity-pink transition-colors">Edit Full Article & Gallery</button>
                                             <button onClick={async () => {
                                                try {
                                                   await VanityAPI.deleteHighlight(h.id);
                                                   setIssueHighlights(issueHighlights.filter(ih => ih.id !== h.id));
                                                   showToast('Story Removed');
                                                } catch (err: any) {
                                                   showToast(`Error: ${err.message}`);
                                                }
                                             }} className="text-red-500 text-[10px] font-black uppercase tracking-widest hover:text-red-700">Remove Story</button>
                                          </div>
                                       </div>
                                    </div>
                                 ))}
                              </div>

                              {/* Highlight Editor Modal */}
                              {editingHighlightId && (
                                 <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
                                    <div className="bg-white w-full max-w-4xl h-[90vh] rounded-3xl overflow-hidden flex flex-col shadow-2xl">
                                       <div className="p-8 border-b flex justify-between items-center bg-gray-50">
                                          <h3 className="font-serif text-2xl font-bold italic">Article Architect</h3>
                                          <div className="flex gap-4">
                                             <button
                                                onClick={() => {
                                                   const h = issueHighlights.find(x => x.id === editingHighlightId);
                                                   if (h) handleSaveInlineHighlight(h);
                                                }}
                                                disabled={isSaving}
                                                className="bg-vanity-pink text-white px-8 py-2 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-black transition-colors shadow-lg disabled:bg-gray-300"
                                             >
                                                {isSaving ? 'Saving...' : 'Save Changes'}
                                             </button>
                                             <button onClick={() => setEditingHighlightId(null)} className="text-gray-400 hover:text-black font-bold">✕ Close</button>
                                          </div>
                                       </div>
                                       {/* Highlight Modal Tabs */}
                                       <div className="px-10 pt-6 flex gap-4 border-b bg-gray-50/50">
                                          <button onClick={() => setHighlightTab('content')} className={`pb-4 px-2 text-[11px] font-black uppercase tracking-widest border-b-2 transition-all ${highlightTab === 'content' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Content & Gallery</button>
                                          <button onClick={() => setHighlightTab('seo')} className={`pb-4 px-2 text-[11px] font-black uppercase tracking-widest border-b-2 transition-all ${highlightTab === 'seo' ? 'border-vanity-pink text-vanity-pink' : 'border-transparent text-gray-400 hover:text-black'}`}>SEO Registry</button>
                                       </div>
                                       <div className="flex-1 overflow-y-auto p-10 space-y-10">
                                          {(() => {
                                             const h = issueHighlights.find(x => x.id === editingHighlightId);
                                             if (!h) return null;
                                             return (
                                                <>
                                                   {highlightTab === 'content' && (
                                                      <div className="space-y-10 animate-fadeIn">
                                                         {/* Metadata */}
                                                         <div className="grid grid-cols-2 gap-8">
                                                            <div className="space-y-4">
                                                               <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Story Slug (URL)</label>
                                                               <input
                                                                  type="text"
                                                                  value={h.slug || ''}
                                                                  onChange={e => setIssueHighlights(issueHighlights.map(ih => ih.id === h.id ? { ...ih, slug: e.target.value } : ih))}
                                                                  className="w-full border p-4 rounded-xl font-mono text-sm"
                                                                  placeholder="my-story-slug"
                                                               />
                                                            </div>
                                                         </div>

                                                         {/* Long Content */}
                                                         <div className="space-y-4">
                                                            <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Full Editorial Content (HTML Supported)</label>
                                                            <RichTextEditor name="content" value={h.content || ''} onChange={val => setIssueHighlights(issueHighlights.map(ih => ih.id === h.id ? { ...ih, content: val } : ih))} height="h-96" />
                                                         </div>

                                                         {/* Gallery Manager */}
                                                         <div className="space-y-6 pt-8 border-t border-gray-100">
                                                            <div className="flex justify-between items-center">
                                                               <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Visual Index (Gallery)</label>
                                                               <button
                                                                  onClick={() => {
                                                                     setUploadTarget('highlight_gallery:' + h.id);
                                                                     legacyFileInputRef.current?.click();
                                                                  }}
                                                                  className="bg-black text-white px-6 py-2 rounded-full text-[10px] font-black uppercase"
                                                               >
                                                                  + Upload Image
                                                               </button>
                                                            </div>
                                                            <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
                                                               {(h.galleryImages || []).map((img, i) => (
                                                                  <div key={i} className="aspect-square bg-gray-100 rounded-xl relative group overflow-hidden">
                                                                     <img src={img} className="w-full h-full object-cover" />
                                                                     <button
                                                                        onClick={() => {
                                                                           const newG = (h.galleryImages || []).filter((_, idx) => idx !== i);
                                                                           setIssueHighlights(issueHighlights.map(ih => ih.id === h.id ? { ...ih, galleryImages: newG } : ih));
                                                                        }}
                                                                        className="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                     >
                                                                        ✕
                                                                     </button>
                                                                  </div>
                                                               ))}
                                                               {(!h.galleryImages || h.galleryImages.length === 0) && (
                                                                  <div className="col-span-full py-8 text-center text-gray-400 text-xs italic border-2 border-dashed rounded-xl">
                                                                     No additional imagery.
                                                                  </div>
                                                               )}
                                                            </div>
                                                         </div>
                                                      </div>
                                                   )}

                                                   {highlightTab === 'seo' && (
                                                      <div className="pt-8 border-t border-gray-100 animate-fadeIn">
                                                         <SEOPanel
                                                            seoTitle={h.seoTitle || ''}
                                                            seoDescription={h.seoDescription || ''}
                                                            seoKeywords={h.seoKeywords || ''}
                                                            ogTitle={h.ogTitle || ''}
                                                            ogDescription={h.ogDescription || ''}
                                                            canonicalUrl={h.canonicalUrl || ''}
                                                            seoScore={h.seoScore}
                                                            slug={h.slug || ''}
                                                            contentForAI={`${h.title} ${h.text} ${h.content}`}
                                                            onChange={(fields) => {
                                                               setIssueHighlights(issueHighlights.map(ih => ih.id === h.id ? { ...ih, ...fields } : ih));
                                                            }}
                                                            baseUrl="https://vanity.com/highlights"
                                                         />
                                                      </div>
                                                   )}
                                                </>
                                             );
                                          })()}
                                       </div>
                                    </div>
                                 </div>
                              )}
                           </div>
                        )}
                        {magSubTab === 'seo' && (
                           <div className="p-16 animate-fadeIn">
                              <div className="flex justify-between items-center mb-8">
                                 <h3 className="font-serif text-3xl font-bold italic">Search Meta Registry</h3>
                                 <button onClick={generateSEOMag} className="bg-vanity-pink text-white px-8 py-3 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg hover:brightness-110 transition-all">Auto-Generate Recommendations</button>
                              </div>
                              <SEOPanel
                                 seoTitle={editingMag.seoTitle || ''}
                                 seoDescription={editingMag.seoDescription || ''}
                                 // seoData removed
                                 ogTitle={editingMag.ogTitle || ''}
                                 ogDescription={editingMag.ogDescription || ''}
                                 canonicalUrl={editingMag.canonicalUrl || ''}
                                 seoKeywords={editingMag.seoKeywords || ''}
                                 slug={editingMag.slug || ''}
                                 contentForAI={`${editingMag.title} ${editingMag.summary} ${editingMag.editorialText}`}
                                 onChange={(fields) => setEditingMag({ ...editingMag, ...fields })}
                                 baseUrl="https://vanity.com/magazine"
                              />
                           </div>
                        )}
                     </div>
                  </div>
               )}

               {/* All Magazines Module */}
               {!editingMag && !editingPage && activeTab === 'all_magazines' && (
                  <div className="bg-white border border-gray-100 rounded-3xl shadow-xl overflow-hidden animate-fadeIn">
                     <div className="p-10 border-b flex justify-between items-center bg-gray-50">
                        <span className="font-black text-[15px] uppercase text-gray-400 tracking-[0.2em]">Anthology Archive</span>
                        <button onClick={() => setEditingMag({ id: '', title: 'Draft Issue', season: 'Draft', year: 2025, status: 'draft', periodText: '', issueDate: '', isCurrent: false, isArchived: false, dateCreated: new Date().toISOString(), dateEdited: new Date().toISOString(), summary: '', coverUrl: '', spreads: [] })} className="bg-black text-white px-10 py-4 rounded-full text-[13px] font-black uppercase tracking-widest">+ New Issue</button>
                     </div>
                     <div className="divide-y divide-gray-100">
                        {allMagazines.map(m => (
                           <div key={m.id} onClick={() => setEditingMag(m)} className="p-10 flex items-center justify-between hover:bg-gray-50 cursor-pointer group transition-all">
                              <div className="flex items-center gap-10">
                                 <img src={m.coverUrl} className="w-16 h-20 object-cover rounded shadow" />
                                 <div>
                                    <div className="font-bold text-2xl text-black flex items-center gap-4">
                                       {m.title}
                                       {m.isCurrent && <span className="text-[10px] bg-vanity-pink text-white px-3 py-1 rounded-full uppercase">Active</span>}
                                    </div>
                                    <div className="text-gray-400 text-xs font-black uppercase tracking-widest mt-1">{m.periodText} &middot; {m.status}</div>
                                 </div>
                              </div>
                              <div className="flex items-center gap-6">
                                 {/* iPhone Style Toggle */}
                                 <div
                                    className={`w-14 h-8 rounded-full p-1 cursor-pointer transition-colors ${m.status === 'published' ? 'bg-green-500' : 'bg-gray-300'}`}
                                    onClick={async (e) => {
                                       e.stopPropagation();
                                       const newStatus = m.status === 'published' ? 'draft' : 'published';
                                       // Optimistic UI
                                       setAllMagazines(allMagazines.map(x => x.id === m.id ? { ...x, status: newStatus } : x));
                                       try {
                                          await VanityAPI.saveMagazine({ ...m, status: newStatus });
                                          showToast(`Status: ${newStatus.toUpperCase()}`);
                                       } catch (err) {
                                          showToast("Failed to update status");
                                          loadRegistry();
                                       }
                                    }}
                                 >
                                    <div className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform ${m.status === 'published' ? 'translate-x-6' : 'translate-x-0'}`} />
                                 </div>
                                 <span className="opacity-0 group-hover:opacity-100 text-vanity-pink font-black text-xs uppercase tracking-widest transition-all">Edit Node</span>
                              </div>
                           </div>
                        ))}
                     </div>
                  </div>
               )}

               {/* Pages Module */}
               {!editingMag && !editingPage && activeTab === 'pages' && (
                  <div className="bg-white border border-gray-100 rounded-3xl shadow-xl overflow-hidden animate-fadeIn">
                     <div className="p-10 border-b flex justify-between items-center bg-gray-50">
                        <span className="font-black text-[15px] uppercase text-gray-400 tracking-[0.2em]">Page Registry</span>
                        <button onClick={() => {
                           setEditingPage({
                              id: '',
                              name: 'New Page',
                              title: 'New Vanity Page',
                              slug: 'new-page',
                              content: '',
                              status: 'draft',
                              showInMenu: false,
                              showInDropdown: false,
                              showInFooter: true,
                              displayOrder: 0
                           });
                           setPageSubTab('content');
                        }} className="bg-black text-white px-10 py-4 rounded-full text-[13px] font-black uppercase tracking-widest">+ Create Page</button>
                     </div>
                     <div className="divide-y divide-gray-100">
                        {allPages.map(p => (
                           <div key={p.id} onClick={() => { setEditingPage(p); setPageSubTab('content'); }} className="p-10 flex items-center justify-between hover:bg-gray-50 cursor-pointer group transition-all">
                              <div className="flex items-center gap-10">
                                 <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center font-black text-gray-400">P</div>
                                 <div className="flex flex-col">
                                    <div className="font-bold text-2xl text-black flex items-center gap-3">
                                       {p.name}
                                       {['contact', 'archive', 'about-us'].includes(p.slug) && (
                                          <span className="text-[9px] bg-black text-white px-2 py-0.5 rounded-full uppercase font-black">System Node</span>
                                       )}
                                    </div>
                                    <div className="text-gray-400 text-sm font-code mt-1 opacity-60">/{p.slug}</div>
                                 </div>
                              </div>

                              <div className="flex items-center gap-6">
                                 {/* Status Toggle */}
                                 <div
                                    className={`w-14 h-8 rounded-full p-1 cursor-pointer transition-colors ${p.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`}
                                    onClick={async (e) => {
                                       e.stopPropagation();
                                       const newStatus = p.status === 'active' ? 'draft' : 'active';

                                       // Optimistic UI
                                       setAllPages(allPages.map(x => x.id === p.id ? { ...x, status: newStatus } : x));

                                       try {
                                          await VanityAPI.savePage({ ...p, status: newStatus });
                                          showToast(`Page: ${newStatus.toUpperCase()}`);
                                       } catch (err) {
                                          showToast("Failed to update status");
                                          loadRegistry();
                                       }
                                    }}
                                 >
                                    <div className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform ${p.status === 'active' ? 'translate-x-6' : 'translate-x-0'}`} />
                                 </div>

                                 {/* Delete Button (Only if NOT active) */}
                                 {p.status !== 'active' && !['contact', 'archive', 'about-us'].includes(p.slug) && (
                                    <button
                                       onClick={async (e) => {
                                          e.stopPropagation();
                                          if (confirm(`Delete page "${p.name}" permanently?`)) {
                                             try {
                                                await VanityAPI.deletePage(p.id);
                                                setAllPages(allPages.filter(x => x.id !== p.id));
                                                showToast("Page Deleted");
                                             } catch (err: any) {
                                                showToast("Delete Failed: " + err.message);
                                             }
                                          }
                                       }}
                                       className="w-10 h-10 bg-red-50 text-red-500 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
                                       title="Delete Page"
                                    >
                                       ✕
                                    </button>
                                 )}
                                 <span className="opacity-0 group-hover:opacity-100 text-vanity-pink font-black text-xs uppercase tracking-widest transition-all">Edit</span>
                              </div>
                           </div>
                        ))}
                     </div>
                  </div >
               )}

               {/* Page Editor (Overhaul) */}
               {
                  editingPage && (
                     <div className="bg-white border border-gray-100 rounded-3xl shadow-xl overflow-hidden animate-fadeIn">
                        <div className="bg-gray-50 border-b p-8 flex gap-8 items-center">
                           {(['content', 'navigation', 'seo'] as const).map(t => (
                              <button key={t} onClick={() => setPageSubTab(t)} className={`px-10 py-4 rounded-full text-[13px] font-black uppercase tracking-widest transition-all ${pageSubTab === t ? 'bg-black text-white shadow-lg' : 'text-gray-400 hover:text-black'}`}>
                                 {t}
                              </button>
                           ))}
                           <div className="flex-1 flex justify-end gap-4 items-center">
                              <button
                                 onClick={() => setEditingPage({ ...editingPage, status: editingPage.status === 'active' ? 'draft' : 'active' })}
                                 className={`px-8 py-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${editingPage.status === 'active' ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}
                              >
                                 {editingPage.status === 'active' ? 'Status: Active' : 'Status: Inactive'}
                              </button>
                              {editingPage.slug && ['contact', 'archive', 'about-us'].includes(editingPage.slug) ? (
                                 <div className="bg-gray-200 text-gray-500 px-8 py-3 rounded-full text-[10px] font-black uppercase tracking-widest cursor-not-allowed">System Protected Node</div>
                              ) : (
                                 <>
                                    {editingPage.status !== 'active' && (
                                       <button
                                          onClick={async () => {
                                             if (confirm('Delete this page permanently?')) {
                                                try {
                                                   const res = await fetch(`/api/pages/${editingPage.id}`, { method: 'DELETE' });
                                                   if (!res.ok) throw new Error('Failed to delete');
                                                   showToast('Page Deleted');
                                                   loadRegistry();
                                                   setEditingPage(null);
                                                } catch (err: any) {
                                                   showToast(`Error: ${err.message}`);
                                                }
                                             }
                                          }}
                                          className="bg-red-50 text-red-600 px-8 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all">
                                          Delete Page
                                       </button>
                                    )}
                                    <button onClick={() => setEditingPage(null)} className="text-gray-400 px-8 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:text-black">Discard</button>
                                    <button onClick={handleSavePage} className="bg-vanity-pink text-white px-10 py-4 rounded-full text-[13px] font-black uppercase tracking-widest shadow-xl hover:brightness-110 transition-all">{isSaving ? 'Saving...' : 'Commit Registry Change'}</button>
                                 </>
                              )}
                           </div>
                        </div>
                        <div className="p-16">
                           {pageSubTab === 'content' && (
                              <div className="space-y-16 animate-fadeIn">
                                 {/* Hero & Meta Section */}
                                 <div className="grid grid-cols-12 gap-16 p-10 bg-gray-50 rounded-3xl border border-gray-100 shadow-sm">
                                    <div className="col-span-8 space-y-10">
                                       <div className="grid grid-cols-2 gap-8">
                                          <div className="space-y-4">
                                             <label className="text-[14px] font-black text-gray-400 uppercase tracking-widest">Internal Name</label>
                                             <input type="text" value={editingPage.name || ''} onChange={e => setEditingPage({ ...editingPage, name: e.target.value })} className="w-full border border-gray-200 p-6 rounded-2xl focus:border-vanity-pink outline-none font-bold text-2xl" />
                                          </div>
                                          <div className="space-y-4">
                                             <label className="text-[14px] font-black text-gray-400 uppercase tracking-widest">URL Slug</label>
                                             <input type="text" value={editingPage.slug || ''} onChange={e => setEditingPage({ ...editingPage, slug: e.target.value })} className="w-full border border-gray-200 p-6 rounded-2xl focus:border-vanity-pink outline-none font-mono text-xl" />
                                          </div>
                                       </div>
                                       <div className="space-y-4">
                                          <label className="text-[14px] font-black text-gray-400 uppercase tracking-widest">Public Hero Title</label>
                                          <input type="text" value={editingPage.title || ''} onChange={e => setEditingPage({ ...editingPage, title: e.target.value })} className="w-full border border-gray-200 p-6 rounded-2xl focus:border-vanity-pink outline-none font-serif text-4xl shadow-sm" />
                                       </div>
                                    </div>
                                    <div className="col-span-4 space-y-10">
                                       <label className="text-[14px] font-black text-gray-400 uppercase tracking-widest text-center block">Hero Imagery</label>
                                       <div onClick={() => { setUploadTarget('page_hero'); sharedInputRef.current?.click(); }} className="aspect-[3/4] bg-white border border-gray-200 rounded-3xl flex items-center justify-center overflow-hidden hover:border-vanity-pink transition-all relative group shadow-md cursor-pointer mx-auto max-w-[200px]">
                                          {editingPage.heroImageUrl ? <img src={editingPage.heroImageUrl} className="w-full h-full object-cover" /> : <span className="font-bold text-gray-300">Select</span>}
                                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                             <span className="text-white text-[9px] font-black uppercase tracking-widest text-center px-4 leading-relaxed">Change Visual</span>
                                          </div>
                                       </div>
                                       {/* Hero Alt Text */}
                                       <div className="space-y-2">
                                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Visual Description (Alt Text)</label>
                                          <input
                                             type="text"
                                             value={editingPage.featuredImageAltText || ''}
                                             onChange={(e) => {
                                                setEditingPage({ ...editingPage, featuredImageAltText: e.target.value });
                                                // Debounced Auto-Save
                                                if ((window as any).heroAltTimer) clearTimeout((window as any).heroAltTimer);
                                                (window as any).heroAltTimer = setTimeout(async () => {
                                                   if (editingPage.heroImageId) {
                                                      await VanityAPI.updateImageAltText(editingPage.heroImageId, e.target.value);
                                                      console.log('Alt Text Autosaved');
                                                   }
                                                }, 1500);
                                             }}
                                             className="w-full text-xs border border-gray-200 p-3 rounded-lg focus:border-vanity-pink outline-none text-center"
                                             placeholder="Describe this image..."
                                          />
                                       </div>
                                    </div>
                                 </div>

                                 {/* Narrative Content */}
                                 <div className="space-y-4">
                                    <label className="text-[14px] font-black text-gray-400 uppercase tracking-widest">Page Narrative (Intro / Content)</label>
                                    <RichTextEditor
                                       name="content"
                                       value={editingPage.content || ''}
                                       onChange={val => setEditingPage({ ...editingPage, content: val })}
                                       height="h-64"
                                    />
                                 </div>

                                 {/* Modular Builder */}
                                 <div className="space-y-12">
                                    <div className="flex items-center justify-between border-b pb-8">
                                       <h3 className="font-serif text-3xl font-bold italic">Page Body Architecture</h3>
                                       <div className="flex gap-4">
                                          <button onClick={() => addModule('text')} className="bg-white border border-black hover:bg-black hover:text-white px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-sm">+ Text Only</button>
                                          <button onClick={() => addModule('text-image', 'left')} className="bg-white border border-black hover:bg-black hover:text-white px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-sm">+ Img Left</button>
                                          <button onClick={() => addModule('text-image', 'right')} className="bg-white border border-black hover:bg-black hover:text-white px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-sm">+ Img Right</button>
                                       </div>
                                    </div>

                                    <div className="space-y-12">
                                       {(editingPage.contentModules || []).map((m, idx) => (
                                          <div key={m.id} className="bg-white border-2 border-gray-50 rounded-3xl p-10 hover:border-gray-100 transition-all shadow-xl space-y-8 relative group">
                                             {/* Module Controls */}
                                             <div className="absolute top-8 right-8 flex gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => moveModule(m.id, 'up')} className="bg-gray-100 p-2 rounded-full hover:bg-black hover:text-white transition-colors">
                                                   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
                                                </button>
                                                <button onClick={() => moveModule(m.id, 'down')} className="bg-gray-100 p-2 rounded-full hover:bg-black hover:text-white transition-colors">
                                                   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                                                </button>
                                                <button onClick={() => deleteModule(m.id)} className="bg-vanity-pink/10 text-vanity-pink p-2 rounded-full hover:bg-vanity-pink hover:text-white transition-colors">
                                                   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>
                                                </button>
                                             </div>

                                             <div className={`grid gap-12 ${m.type === 'text-image' ? 'grid-cols-12' : 'grid-cols-1'}`}>
                                                {/* Text Part */}
                                                <div className={m.type === 'text-image' ? (m.imagePosition === 'left' ? 'col-span-8 order-2' : 'col-span-8 order-1') : ''}>
                                                   <div className="space-y-8">
                                                      <div className="space-y-2">
                                                         <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Section Heading</label>
                                                         <input type="text" placeholder="Title for this block..." value={m.title || ''} onChange={e => updateModule(m.id, { title: e.target.value })} className="w-full text-2xl font-serif italic border-0 border-b border-gray-100 pb-2 focus:border-vanity-pink outline-none" />
                                                      </div>
                                                      <div className="space-y-2">
                                                         <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Narrative Text</label>
                                                         <RichTextEditor value={m.text || ''} onChange={val => updateModule(m.id, { text: val })} height="h-48" />
                                                      </div>
                                                   </div>
                                                </div>

                                                {/* Image Part */}
                                                {m.type === 'text-image' && (
                                                   <div className={m.imagePosition === 'left' ? 'col-span-4 order-1' : 'col-span-4 order-2'}>
                                                      <div onClick={() => { setUploadTarget('module_image'); setModuleTargetId(m.id); sharedInputRef.current?.click(); }} className="aspect-square bg-gray-50 border-2 border-dashed border-gray-200 rounded-3xl flex items-center justify-center overflow-hidden hover:border-vanity-pink transition-all relative group cursor-pointer shadow-inner">
                                                         {m.imageUrl ? <img src={m.imageUrl} className="w-full h-full object-cover" /> : <span className="font-bold text-gray-300">Select Image</span>}
                                                         <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                            <span className="text-white font-black uppercase text-[10px] tracking-widest text-center px-4 leading-relaxed">Change Asset</span>
                                                         </div>
                                                      </div>
                                                      {/* Module Alt Text */}
                                                      <div className="mt-4 space-y-2">
                                                         <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Image Description</label>
                                                         <input
                                                            type="text"
                                                            value={m.altText || ''}
                                                            onChange={e => updateModule(m.id, { altText: e.target.value })}
                                                            className="w-full text-xs border border-gray-100 p-2 rounded-lg focus:border-vanity-pink outline-none bg-gray-50 focus:bg-white"
                                                            placeholder="Describe image..."
                                                         />
                                                      </div>
                                                   </div>
                                                )}
                                             </div>
                                          </div>
                                       ))}


                                    </div>
                                 </div>

                                 {/* Legacy Support Section */}
                                 <div className="pt-16 mt-16 border-t border-gray-100 opacity-40 hover:opacity-100 transition-opacity">
                                    <details className="cursor-pointer group">
                                       <summary className="font-black text-[11px] uppercase tracking-widest text-gray-400 list-none flex items-center gap-2">
                                          <svg className="group-open:rotate-180 transition-transform" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m6 9 6 6 6-6" /></svg>
                                          Legacy Direct-HTML Content (Fallback)
                                       </summary>
                                       <div className="mt-8 space-y-4">
                                          <textarea rows={8} value={editingPage.content || ''} onChange={e => setEditingPage({ ...editingPage, content: e.target.value })} className="w-full border border-gray-100 p-8 rounded-3xl font-mono text-sm focus:border-vanity-pink outline-none shadow-inner bg-gray-50 focus:bg-white transition-all" />
                                       </div>
                                    </details>
                                 </div>
                              </div>
                           )}
                           {pageSubTab === 'navigation' && (
                              <div className="max-w-2xl space-y-12 animate-fadeIn">
                                 <h3 className="font-serif text-3xl font-bold italic">Menu Visibility Archetype</h3>
                                 <div className="space-y-6">
                                    <label className="bg-gray-50 p-8 rounded-3xl flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-all border border-transparent hover:border-vanity-pink/30">
                                       <div className="flex flex-col gap-1">
                                          <span className="font-black uppercase text-xs tracking-widest">Top Level Menu</span>
                                          <span className="text-gray-400 text-[10px] uppercase">Displays in the primary header navigation row</span>
                                       </div>
                                       <input type="checkbox" checked={editingPage.showInMenu} onChange={e => setEditingPage({ ...editingPage, showInMenu: e.target.checked })} className="w-8 h-8 accent-vanity-pink" />
                                    </label>
                                    <label className="bg-gray-50 p-8 rounded-3xl flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-all border border-transparent hover:border-vanity-pink/30">
                                       <div className="flex flex-col gap-1">
                                          <span className="font-black uppercase text-xs tracking-widest">Dropdown Archive</span>
                                          <span className="text-gray-400 text-[10px] uppercase">Displays inside the hamburger secondary menu</span>
                                       </div>
                                       <input type="checkbox" checked={editingPage.showInDropdown} onChange={e => setEditingPage({ ...editingPage, showInDropdown: e.target.checked })} className="w-8 h-8 accent-vanity-pink" />
                                    </label>
                                    <label className="bg-gray-50 p-8 rounded-3xl flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-all border border-transparent hover:border-vanity-pink/30">
                                       <div className="flex flex-col gap-1">
                                          <span className="font-black uppercase text-xs tracking-widest">Footer Registry</span>
                                          <span className="text-gray-400 text-[10px] uppercase">Displays in the site-wide footer utility menu</span>
                                       </div>
                                       <input type="checkbox" checked={editingPage.showInFooter} onChange={e => setEditingPage({ ...editingPage, showInFooter: e.target.checked })} className="w-8 h-8 accent-vanity-pink" />
                                    </label>
                                 </div>
                                 <div className="space-y-6 bg-black text-white p-12 rounded-3xl shadow-2xl">
                                    <label className="text-[11px] font-black text-vanity-pink uppercase tracking-[0.4em]">Display Sequence Index</label>
                                    <div className="flex items-center gap-10">
                                       <input type="number" value={editingPage.displayOrder} onChange={e => setEditingPage({ ...editingPage, displayOrder: parseInt(e.target.value) || 0 })} className="bg-white/10 border-0 rounded-2xl p-6 text-5xl font-serif text-white w-32 text-center" />
                                       <p className="text-xs text-gray-500 uppercase font-bold tracking-widest leading-relaxed">Lower numbers appear first in all navigation groups.</p>
                                    </div>
                                 </div>
                              </div>
                           )}
                           {pageSubTab === 'seo' && (
                              <div className="max-w-3xl space-y-12 animate-fadeIn">
                                 <div className="flex justify-between items-center mb-8">
                                    <h3 className="font-serif text-3xl font-bold italic">Search Meta Registry</h3>
                                 </div>
                                 {/* SEO */}
                                 <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                                    <SEOPanel
                                       seoTitle={editingPage?.seoTitle || ''}
                                       seoDescription={editingPage?.seoDescription || ''}
                                       seoKeywords={editingPage?.seoKeywords || ''}
                                       ogTitle={editingPage?.ogTitle || ''}
                                       ogDescription={editingPage?.ogDescription || ''}
                                       canonicalUrl={editingPage?.canonicalUrl || ''}
                                       seoScore={editingPage?.seoScore}
                                       slug={editingPage?.slug || ''}
                                       contentForAI={editingPage?.content || ''}
                                       baseUrl="https://vanity.co.at"
                                       onChange={(fields) => setEditingPage(prev => (!prev ? null : {
                                          ...prev,
                                          ...fields
                                       }))}
                                    />
                                 </div>
                              </div>
                           )}
                        </div>
                     </div>
                  )
               }


               {/* Categories Module */}
               {
                  !editingMag && !editingPage && activeTab === 'categories' && (
                     <div className="bg-white border border-gray-100 rounded-3xl p-16 space-y-12 shadow-xl animate-fadeIn">
                        <div className="flex justify-between items-center border-b pb-10">
                           <span className="font-black text-[15px] uppercase text-gray-500 tracking-[0.3em]">Classifications</span>
                           {!isCreatingCategory ? (
                              <button onClick={() => { setIsCreatingCategory(true); setCatTempName(''); setCatTempSlug(''); }} className="bg-black text-white px-10 py-4 rounded-full text-[13px] font-black uppercase tracking-widest shadow-lg">+ Add New</button>
                           ) : (
                              <div className="flex gap-4 animate-fadeIn w-full max-w-2xl bg-gray-50 p-6 rounded-2xl border border-vanity-pink/30">
                                 <div className="flex-1 space-y-2">
                                    <input type="text" value={catTempName} onChange={e => { setCatTempName(e.target.value); if (!catTempSlug) setCatTempSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')); }} placeholder="Category Name" autoFocus className="w-full border p-3 rounded-xl font-bold outline-none focus:border-vanity-pink" />
                                    <input type="text" value={catTempSlug || ''} onChange={e => setCatTempSlug(e.target.value)} placeholder="url-slug" className="w-full border p-3 rounded-xl font-mono text-xs outline-none focus:border-vanity-pink" />
                                 </div>
                                 <div className="flex flex-col gap-2">
                                    <button onClick={handleAddCategory} className="bg-vanity-pink text-white px-6 py-3 rounded-xl text-xs font-black uppercase shadow-lg">Create</button>
                                    <button onClick={() => setIsCreatingCategory(false)} className="bg-white text-gray-400 border px-6 py-3 rounded-xl text-xs font-black uppercase">Cancel</button>
                                 </div>
                              </div>
                           )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                           {allCategories.map(c => (
                              <div key={c.id} className={`p-10 bg-gray-50 border rounded-3xl transition-all ${editingCategory === c.id ? 'border-vanity-pink shadow-xl ring-4 ring-vanity-pink/5 z-10' : 'border-gray-200 hover:shadow-lg'}`}>
                                 {editingCategory === c.id ? (
                                    <div className="space-y-6">
                                       <div className="space-y-2">
                                          <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Display Name</label>
                                          <input type="text" value={catTempName} onChange={e => setCatTempName(e.target.value)} className="w-full bg-white border p-3 rounded-xl font-bold text-lg outline-none focus:ring-1 focus:ring-vanity-pink" />
                                       </div>
                                       <div className="space-y-2">
                                          <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest">URL Slug</label>
                                          <input type="text" value={catTempSlug || ''} onChange={e => setCatTempSlug(e.target.value)} className="w-full bg-white border p-3 rounded-xl font-mono text-xs outline-none focus:ring-1 focus:ring-vanity-pink" />
                                       </div>
                                       <label className="flex items-center justify-between bg-white p-3 rounded-xl border border-gray-100 cursor-pointer">
                                          <span className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Active State</span>
                                          <input type="checkbox" checked={c.active} onChange={async (e) => {
                                             try {
                                                // Optimistic Local Update
                                                setAllCategories(prev => prev.map(x => x.id === c.id ? { ...x, active: e.target.checked } : x));
                                                await VanityAPI.saveCategory({ ...c, active: e.target.checked });
                                             } catch (err) { showToast('Status Update Failed'); loadRegistry(); }
                                          }} className="w-5 h-5 accent-vanity-pink" />
                                       </label>

                                       <div className="pt-4 border-t border-gray-100">
                                          <SEOPanel
                                             seoTitle={c.seoTitle || ''}
                                             seoDescription={c.seoDescription || ''}
                                             seoKeywords={c.seoKeywords || ''}
                                             ogTitle={c.ogTitle || ''}
                                             ogDescription={c.ogDescription || ''}
                                             canonicalUrl={c.canonicalUrl || ''}
                                             slug={c.slug || ''}
                                             contentForAI={`${c.name}`}
                                             onChange={(fields) => {
                                                setAllCategories(prev => prev.map(x => x.id === c.id ? { ...x, ...fields } : x));
                                             }}
                                             baseUrl="https://vanity.com/categories"
                                             compact={true}
                                          />
                                       </div>

                                       <div className="flex gap-3 pt-4 border-t border-gray-200">
                                          <button onClick={() => commitCategoryEdit(c.id)} className="flex-1 bg-black text-white py-3 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-gray-800 transition-colors">Save</button>
                                          <button onClick={() => setEditingCategory(null)} className="flex-1 bg-white border py-3 rounded-lg text-xs font-black uppercase tracking-widest text-gray-500 hover:text-black transition-colors">Cancel</button>
                                       </div>
                                       <button
                                          onClick={async () => {
                                             if (confirm(`Ideally verify if any existing articles rely on "${c.name}" first.\n\nProceed to delete?`)) {
                                                try {
                                                   await VanityAPI.deleteCategory(c.id);
                                                   setAllCategories(prev => prev.filter(x => x.id !== c.id));
                                                   showToast('Category Deleted');
                                                } catch (err: any) { showToast(err.message); }
                                             }
                                          }}
                                          className="w-full text-center text-[9px] font-black text-red-400 uppercase tracking-widest hover:text-red-600 mt-2 block"
                                       >
                                          Delete Classification
                                       </button>
                                    </div>
                                 ) : (
                                    <div className="flex items-center justify-between cursor-pointer group" onClick={() => startEditCategory(c)}>
                                       <div className="space-y-1">
                                          <div className="font-black text-black text-2xl uppercase group-hover:text-vanity-pink transition-colors">{c.name}</div>
                                          <div className="text-[10px] text-gray-400 font-mono tracking-widest uppercase">/{c.slug}</div>
                                       </div>
                                       <div
                                          onClick={(e) => {
                                             e.stopPropagation();
                                             // Quick Toggle
                                             const newStatus = !c.active;
                                             setAllCategories(prev => prev.map(x => x.id === c.id ? { ...x, active: newStatus } : x));
                                             VanityAPI.saveCategory({ ...c, active: newStatus }).catch(() => {
                                                showToast('Toggle Failed');
                                                loadRegistry();
                                             });
                                          }}
                                          className={`w-3 h-3 rounded-full transition-all hover:scale-125 ${c.active ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.4)]' : 'bg-gray-300'}`}
                                          title="Toggle Active Status"
                                       />
                                    </div>
                                 )}
                              </div>
                           ))}
                        </div>
                     </div>
                  )
               }


               {/* Footer Module */}
               {
                  !editingMag && !editingPage && activeTab === 'footer' && footerConfig && (
                     <div className="bg-white border border-gray-100 rounded-3xl p-16 space-y-16 shadow-xl animate-fadeIn">
                        <div className="border-b pb-10 flex justify-between items-center">
                           <h2 className="font-serif text-5xl font-bold">Footer Registry</h2>
                           <button onClick={() => VanityAPI.saveFooterConfig(footerConfig).then(() => showToast('Footer Saved'))} className="bg-black text-white px-10 py-4 rounded-full text-[13px] font-black uppercase tracking-widest shadow-xl">Update Registry</button>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                           <div className="space-y-8">
                              <div className="space-y-4">
                                 <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Registry Title</label>
                                 <input type="text" value={footerConfig.title || ''} onChange={e => setFooterConfig({ ...footerConfig, title: e.target.value })} className="w-full border p-4 rounded-xl font-bold text-xl shadow-inner outline-none focus:border-vanity-pink" />
                              </div>
                              <div className="space-y-4">
                                 <label className="text-xs font-black text-gray-400 uppercase tracking-widest">About Narrative</label>
                                 <textarea rows={5} value={footerConfig.aboutText || ''} onChange={e => setFooterConfig({ ...footerConfig, aboutText: e.target.value })} className="w-full border p-4 rounded-xl text-lg shadow-inner outline-none focus:border-vanity-pink" />
                              </div>
                           </div>
                           <div className="space-y-8">
                              <div className="space-y-4">
                                 <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Public Correspondence</label>
                                 <input type="text" value={footerConfig.email || ''} onChange={e => setFooterConfig({ ...footerConfig, email: e.target.value })} className="w-full border p-4 rounded-xl font-mono shadow-inner outline-none focus:border-vanity-pink mb-2" placeholder="Email Address" />
                                 <input type="text" value={footerConfig.phone || ''} onChange={e => setFooterConfig({ ...footerConfig, phone: e.target.value })} className="w-full border p-4 rounded-xl font-mono shadow-inner outline-none focus:border-vanity-pink" placeholder="Phone Number" />
                              </div>
                              <div className="space-y-4">
                                 <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Digital Presence (Social Mesh)</label>
                                 <div className="grid grid-cols-1 gap-2">
                                    <input type="text" value={footerConfig.instagram || ''} onChange={e => setFooterConfig({ ...footerConfig, instagram: e.target.value })} className="w-full border p-4 rounded-xl font-mono shadow-inner outline-none focus:border-vanity-pink" placeholder="Instagram URL" />
                                    <input type="text" value={footerConfig.facebook || ''} onChange={e => setFooterConfig({ ...footerConfig, facebook: e.target.value })} className="w-full border p-4 rounded-xl font-mono shadow-inner outline-none focus:border-vanity-pink" placeholder="Facebook URL" />
                                    <input type="text" value={footerConfig.linkedin || ''} onChange={e => setFooterConfig({ ...footerConfig, linkedin: e.target.value })} className="w-full border p-4 rounded-xl font-mono shadow-inner outline-none focus:border-vanity-pink" placeholder="LinkedIn URL" />
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  )
               }

               {/* User Module */}
               {
                  !editingMag && !editingPage && activeTab === 'users' && (
                     <div className="bg-white border border-gray-100 rounded-3xl p-16 space-y-12 shadow-xl animate-fadeIn">
                        <div className="flex justify-between items-center border-b pb-10">
                           <span className="font-black text-[15px] uppercase text-gray-400 tracking-[0.2em]">Administrators</span>
                           <button onClick={() => {
                              const e = prompt('New Admin Email?');
                              if (e) VanityAPI.saveAdminUser({ email: e, name: e.split('@')[0], role: 'admin', isActive: true }).then(() => { loadRegistry(); showToast('User Created'); });
                           }} className="bg-black text-white px-10 py-4 rounded-full text-[13px] font-black uppercase tracking-widest shadow-lg">+ Grant Access</button>
                        </div>
                        <div className="divide-y divide-gray-100">
                           {allUsers.map(u => (
                              <div key={u.email} className="py-8 flex items-center justify-between">
                                 <div className="flex items-center gap-6">
                                    <div className="w-12 h-12 bg-vanity-pink/10 rounded-full flex items-center justify-center font-serif text-vanity-pink font-bold">{u.name.charAt(0)}</div>
                                    <div className="flex flex-col">
                                       <div className="font-bold text-black text-lg">{u.name}</div>
                                       <div className="text-sm text-gray-400 font-mono">{u.email}</div>
                                    </div>
                                 </div>
                                 <div className="flex items-center gap-4">
                                    <button onClick={() => { setPasswordModal({ user: u }); setNewPassword(''); }} className="text-[10px] font-black uppercase tracking-widest px-6 py-2 rounded-full border border-gray-200 hover:bg-black hover:text-white transition-all">Password</button>
                                    <button onClick={() => VanityAPI.saveAdminUser({ ...u, isActive: !u.isActive }).then(loadRegistry)} className={`text-[10px] font-black uppercase tracking-widest px-6 py-2 rounded-full border transition-all ${u.isActive ? 'border-green-500 text-green-700' : 'border-red-500 text-red-700'}`}>{u.isActive ? 'Active' : 'Suspended'}</button>
                                 </div>
                              </div>
                           ))}
                        </div>
                     </div>
                  )
               }

               {/* Highlights Module */}
               {
                  !editingMag && !editingPage && activeTab === 'highlights' && (
                     <div className="animate-fadeIn w-full max-w-7xl mx-auto">
                        <AdminHighlights />
                     </div>
                  )
               }

            </div >
         </main >

         {/* OVERLAYS & FEEDBACK */}
         {
            lightbox && (
               <div className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-24 cursor-zoom-out animate-fadeIn" onClick={() => setLightbox(null)}>
                  <img src={lightbox} className="max-w-full max-h-full shadow-3xl border-[15px] border-white/5 object-contain" />
               </div>
            )
         }

         {
            toast && (
               <div className="fixed bottom-20 right-20 z-[120] bg-black text-white px-12 py-6 rounded-full shadow-3xl border-l-[10px] border-vanity-pink font-black text-[13px] uppercase tracking-[0.4em] animate-fadeIn">
                  {toast}
               </div>
            )
         }

         {
            confirmModal && (
               <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-[5px] p-8 animate-fadeIn">
                  <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-3xl">
                     <div className="p-8 border-b bg-gray-50 font-black text-xs uppercase tracking-widest text-gray-400">{confirmModal.title}</div>
                     <div className="p-10 text-lg font-medium">{confirmModal.msg}</div>
                     <div className="p-6 bg-gray-50 flex justify-end gap-4">
                        <button onClick={() => setConfirmModal(null)} className="px-6 py-3 text-xs font-black uppercase">Cancel</button>
                        <button onClick={confirmModal.onConfirm} className="px-6 py-3 bg-red-600 text-white rounded-lg text-xs font-black uppercase">Confirm</button>
                     </div>
                  </div>
               </div>
            )
         }

         {
            passwordModal && (
               <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-[5px] p-8 animate-fadeIn">
                  <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-3xl">
                     <div className="p-8 border-b bg-gray-50 font-black text-xs uppercase tracking-widest text-gray-400">Security Update</div>
                     <div className="p-10 space-y-4">
                        <div className="text-sm font-bold text-black">Set new password for {passwordModal.user.name}</div>
                        <input type="password" value={newPassword} autoFocus onChange={e => setNewPassword(e.target.value)} placeholder="New Password..." className="w-full border p-4 rounded-xl font-bold bg-gray-50 outline-none focus:border-vanity-pink" />
                     </div>
                     <div className="p-6 bg-gray-50 flex justify-end gap-4">
                        <button onClick={() => setPasswordModal(null)} className="px-6 py-3 text-xs font-black uppercase">Cancel</button>
                        <button onClick={() => {
                           if (!newPassword) return;
                           VanityAPI.saveAdminUser({ ...passwordModal.user, password: newPassword } as any).then(() => { showToast('Password Updated'); setPasswordModal(null); });
                        }} className="px-6 py-3 bg-black text-white rounded-lg text-xs font-black uppercase">Update</button>
                     </div>
                  </div>
               </div>
            )
         }

         {/* STANDARD UPLOADER COMPONENT (Golden Standard) */}
         {
            showStandardUploader && stashedFile && (
               <StandardImageUploader
                  isOpen={showStandardUploader}
                  onClose={() => { setShowStandardUploader(false); setStashedFile(null); if (sharedInputRef.current) sharedInputRef.current.value = ''; }}
                  initialFile={stashedFile}
                  aspectRatio={uploadTarget === 'home_hero' ? 16 / 9 : (uploadTarget === 'module_image' ? 1 : (uploadTarget === 'cover' ? 3 / 4 : 3 / 4))} // Cover = 3:4
                  entityType={uploadTarget === 'home_hero' ? 'homepage' : (uploadTarget === 'cover' ? 'magazine_cover' : 'page')}
                  entityId={uploadTarget === 'home_hero' ? '00000000-0000-0000-0000-000000000001' : (uploadTarget === 'cover' && editingMag ? editingMag.id : (editingPage?.id || ''))}
                  role={uploadTarget === 'module_image' ? 'segment' : (uploadTarget === 'cover' ? 'cover' : 'hero')}
                  modalTitle={uploadTarget === 'home_hero' ? "Update Homepage Hero" : (uploadTarget === 'cover' ? "Anthology Cover Compositor" : "Update Page Hero")}
                  onUploadComplete={(img) => {
                     if (uploadTarget === 'home_hero' && homepageConfig) {
                        const newConfig = { ...homepageConfig, heroImageUrl: img.url_full || img.url_original, heroImageId: img.id };
                        setHomepageConfig(newConfig);
                        VanityAPI.saveHomepageConfig(newConfig).then(() => showToast('Homepage Updated')).catch(() => showToast('Save Failed'));
                     } else if (uploadTarget === 'page_hero' && editingPage) {
                        setEditingPage({ ...editingPage, heroImageUrl: img.url_full || img.url_original, heroImageId: img.id });
                        showToast('Hero Linked');
                     } else if (uploadTarget === 'module_image' && editingPage && moduleTargetId) {
                        updateModule(moduleTargetId, { imageUrl: img.url_full || img.url_original, imageId: img.id });
                        showToast('Module Image Linked');
                     } else if (uploadTarget === 'cover' && editingMag) {
                        // MAGAZINE COVER GOLDEN STANDARD
                        setEditingMag({ ...editingMag, coverUrl: img.url_full || img.url_original, coverImageId: img.id });
                        showToast('Cover Standardized');
                     }
                     setShowStandardUploader(false);
                     setStashedFile(null);
                  }}
               />
            )
         }

         {/* Hidden Inputs */}
         <input type="file" ref={sharedInputRef} className="hidden" accept="image/*" onChange={(e) => {
            if (e.target.files?.[0]) {
               setStashedFile(e.target.files[0]);
               setShowStandardUploader(true);
            }
         }} />
         <input type="file" ref={legacyFileInputRef} className="hidden" onChange={handleLegacyFileUpload} />
      </div >
   );
};

export default AdminAtelier;
